import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, Outlet, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { brandVars } from '../../lib/brandColor';

/**
 * Layout for the public hosted booking flow (`/:orgSlug/...`). Renders an
 * org-branded header (logo top-left, "Powered by Invitey") and applies the
 * org's brand color to the CTA buttons via scoped CSS variables. The global
 * Invitey topbar is suppressed on these paths (see App.tsx) so this is the only
 * header the customer sees.
 */
type OrgBrand = {
  name: string;
  brand_name: string | null;
  brand_logo_url: string | null;
  brand_color: string | null;
  slug: string;
};

export default function BookingLayout() {
  const { orgSlug } = useParams();
  const [brand, setBrand] = useState<OrgBrand | null>(null);

  useEffect(() => {
    if (!orgSlug) return;
    let cancelled = false;
    api<OrgBrand>(`/book/${encodeURIComponent(orgSlug)}`)
      .then((b) => {
        if (!cancelled) setBrand(b);
      })
      .catch(() => {
        // Org not found / network — fall back to a neutral header.
        if (!cancelled) setBrand(null);
      });
    return () => {
      cancelled = true;
    };
  }, [orgSlug]);

  const name = brand?.brand_name || brand?.name || '';
  const logo = brand?.brand_logo_url ?? null;
  const style = brandVars(brand?.brand_color) as CSSProperties;

  return (
    <div className="booking-shell" style={style}>
      <header className="booking-header">
        <Link to={`/${orgSlug ?? ''}`} className="booking-header-brand" aria-label={name || 'Home'}>
          {logo ? (
            <img src={logo} alt={name} className="booking-logo" />
          ) : (
            <span className="booking-brand-name">{name || 'Book an appointment'}</span>
          )}
        </Link>
        <a
          href="https://invitey.ca"
          className="booking-powered"
          target="_blank"
          rel="noopener noreferrer"
        >
          Powered by Invitey
        </a>
      </header>
      <Outlet />
    </div>
  );
}
